import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog';
import React, { useState } from 'react';

export interface TimelineDetails {
    name: string;
    description: string;
    beats: {
        title: string;
        description: string;
        type: string;
    }[];
    recommendedChapters: number | null;
}

export interface TimelinePreviewProps {
    timelineDetails: TimelineDetails | null;
}

export const TimelinePreview: React.FC<TimelinePreviewProps> = ({
    timelineDetails
}) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    if (!timelineDetails) {
        return null;
    }

    return (
        <>
            {/* Preview Card */}
            <div
                className="mt-4 border rounded-md p-3 bg-muted/30 max-h-[220px] overflow-y-auto cursor-pointer hover:bg-muted/40 transition-colors"
                onClick={() => setIsDialogOpen(true)}
            >
                <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium">
                        {timelineDetails.name}
                    </h3>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsDialogOpen(true);
                        }}
                    >
                        View Details
                    </Button>
                </div>
                <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">
                        {timelineDetails.description}
                    </p>
                    <div className="flex overflow-x-auto gap-1.5 pb-2 mt-2">
                        {timelineDetails.beats.slice(0, 3).map((beat, i) => (
                            <div key={i} className="shrink-0 bg-background border rounded-sm p-2 w-28 h-14">
                                <p className="text-xs font-medium truncate">{beat.title}</p>
                                <p className="text-[10px] text-muted-foreground line-clamp-1 mt-0.5">
                                    {beat.description}
                                </p>
                            </div>
                        ))}
                        {timelineDetails.beats.length > 3 && (
                            <div className="shrink-0 flex items-center justify-center bg-muted/40 rounded-sm w-10">
                                <span className="text-xs text-muted-foreground">
                                    +{timelineDetails.beats.length - 3}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Details Dialog */}
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="sm:max-w-[500px] w-[calc(100%-2rem)] mx-auto max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-lg">
                            {timelineDetails.name}
                        </DialogTitle>
                        <DialogDescription className="text-sm">
                            {timelineDetails.description}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-2">
                        <p className="text-sm">
                            This template will create {timelineDetails.beats.length} plot cards
                            across {timelineDetails.recommendedChapters} chapters.
                        </p>

                        <div className="space-y-3 mt-4">
                            <h3 className="text-sm font-medium">Story Beats</h3>
                            <div className="grid gap-3">
                                {timelineDetails.beats.map((beat, index) => (
                                    <div key={index} className="border rounded-md p-3 bg-background">
                                        <div className="flex items-center justify-between mb-1">
                                            <h4 className="text-sm font-medium">{beat.title}</h4>
                                            <Badge variant="outline" className="text-[10px]">
                                                {beat.type}
                                            </Badge>
                                        </div>
                                        <p className="text-xs text-muted-foreground">{beat.description}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};